import { fDate } from '@/utils/format-date'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { fNumber } from '@/utils/format-number'
import { Card, CardContent } from '@/components/ui/card'
import {
  MoreHorizontal,
  Pencil,
  Trash2,
  Image as ImageIcon,
  Settings,
} from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

import type { Privilege } from './privilege.types'

import { PrivilegeType } from './privilege.types'

interface PrivilegeTableProps {
  privileges: Privilege[]
  loading: boolean
  onEdit: (privilege: Privilege) => void
  onRedeemCondition: (privilege: Privilege) => void
  onDelete: (privilege: Privilege) => void
}

export default function PrivilegeTable({
  privileges,
  loading,
  onEdit,
  onRedeemCondition,
  onDelete,
}: PrivilegeTableProps) {
  const getStatusBadge = (isEnable: boolean) => {
    return isEnable ? (
      <Badge variant="default">เปิดใช้งาน</Badge>
    ) : (
      <Badge variant="secondary">ปิดใช้งาน</Badge>
    )
  }

  const getTypeBadge = (type: PrivilegeType) => {
    switch (type) {
      case PrivilegeType.ITEM:
        return <Badge variant="outline">สินค้า</Badge>
      case PrivilegeType.COUPON:
        return <Badge variant="secondary">คูปอง</Badge>
      default:
        return <Badge variant="outline">ไม่ระบุ</Badge>
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="flex items-center space-x-4">
                <div className="h-12 w-12 rounded-lg bg-muted animate-pulse" />
                <div className="space-y-2 flex-1">
                  <div className="h-4 bg-muted rounded animate-pulse" />
                  <div className="h-3 bg-muted rounded w-3/4 animate-pulse" />
                </div>
                <div className="h-8 w-20 bg-muted rounded animate-pulse" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (privileges.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <div className="space-y-4">
            <div className="mx-auto h-12 w-12 text-muted-foreground">
              <ImageIcon className="h-full w-full" />
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-medium">ไม่พบของรางวัล</h3>
              <p className="text-sm text-muted-foreground">
                ยังไม่มีของรางวัลในระบบ หรือไม่พบข้อมูลตามเงื่อนไขที่ค้นหา
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="p-0">
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[80px]">รูปภาพ</TableHead>
              <TableHead>ชื่อของรางวัล</TableHead>
              <TableHead className="text-center">ประเภท</TableHead>
              <TableHead className="text-center">คงเหลือ</TableHead>
              <TableHead className="text-center">สถานะ</TableHead>
              <TableHead className="text-center">มีเงื่อนไข</TableHead>
              <TableHead className="text-center">วันที่สร้าง</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {privileges.map((privilege) => (
              <TableRow key={privilege.id}>
                <TableCell>
                  <div className="h-12 w-12 rounded-lg overflow-hidden bg-muted flex items-center justify-center">
                    {privilege.imageHeaderUrl ? (
                      <img
                        src={privilege.imageHeaderUrl}
                        alt={privilege.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <ImageIcon className="h-6 w-6 text-muted-foreground" />
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <div className="font-medium">{privilege.name}</div>
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  {getTypeBadge(privilege.type)}
                </TableCell>
                <TableCell className="text-center">
                  <span
                    className={`font-medium ${
                      privilege.totalQuantity - privilege.claimedQuantity === 0
                        ? 'text-destructive'
                        : privilege.totalQuantity - privilege.claimedQuantity <
                            privilege.totalQuantity * 0.2
                          ? 'text-orange-600'
                          : 'text-green-600'
                    }`}
                  >
                    {fNumber(
                      privilege.totalQuantity - privilege.claimedQuantity,
                    )}
                  </span>
                </TableCell>
                <TableCell className="text-center">
                  {getStatusBadge(privilege.isEnable)}
                </TableCell>
                <TableCell className="text-center">
                  {Array.isArray(privilege.redeemConditions) &&
                  privilege.redeemConditions.length > 0 ? (
                    <span className="font-medium">
                      {privilege.redeemConditions.length}
                    </span>
                  ) : (
                    <span className="text-green-600">ฟรี</span>
                  )}
                </TableCell>
                <TableCell className="text-center text-sm text-muted-foreground">
                  {fDate(privilege.createdAt)}
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onEdit(privilege)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        แก้ไข
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => onRedeemCondition(privilege)}
                      >
                        <Settings className="mr-2 h-4 w-4" />
                        เงื่อนไข
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => onDelete(privilege)}
                        className="text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        ลบ
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
